import React, { useState } from 'react';
import { useLocale } from '@platform/core';
import { Project } from '../../types/budget.types';
import { ProjectRow } from './ProjectRow';
import { ProjectDetails } from './ProjectDetails';
import { ProjectFormModal } from './ProjectFormModal';

interface ProjectsTableProps {
  projects: Project[];
  currency?: string;
  onDeleteProject: (projectId: string) => void;
}

export const ProjectsTable: React.FC<ProjectsTableProps> = ({
  projects,
  currency,
  onDeleteProject,
}) => {
  const { t } = useLocale();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [editingProject, setEditingProject] = useState<Project | null>(null);

  const totalPages = Math.ceil(projects.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedProjects = projects.slice(startIndex, startIndex + pageSize);

  const handlePageChange = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  return (
    <div className="projects-table-container">
      <div className="table-wrapper">
        <table className="projects-table">
          <thead>
            <tr>
              <th className="col-expand"></th>
              <th>{t('budget.projects.name')}</th>
              <th>{t('budget.projects.client')}</th>
              <th>{t('budget.projects.projectManager')}</th>
              <th>{t('budget.projects.manager')}</th>
              <th>{t('budget.projects.contract')}</th>
              <th>{t('budget.projects.status')}</th>
              <th className="text-end">{t('budget.projects.budget')}</th>
              <th className="text-end">{t('budget.projects.profit')}</th>
              <th className="text-end">{t('budget.projects.profitPercent')}</th>
              <th title={t('budget.projects.timeConsumedTooltip')}>{t('budget.projects.timeConsumed')}</th>
              <th title={t('budget.projects.budgetConsumedTooltip')}>{t('budget.projects.budgetConsumed')}</th>
              <th title={t('budget.projects.invoicesTooltip')}>{t('budget.projects.invoicesIssued')}</th>
              <th className="col-actions"></th>
            </tr>
          </thead>
          <tbody>
            {paginatedProjects.map((project) => (
              <ProjectRow
                key={project.id}
                project={project}
                currency={currency}
                onDelete={onDeleteProject}
                expandedContent={
                  <ProjectDetails
                    project={project}
                    currency={currency}
                    onEdit={() => setEditingProject(project)}
                  />
                }
              />
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <div className="pagination-info">
            {t('common.showing')} {startIndex + 1}-{Math.min(startIndex + pageSize, projects.length)} {t('common.of')} {projects.length}
          </div>

          <div className="pagination-controls">
            <button
              className="pagination-btn"
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
            >
              «
            </button>
            <button
              className="pagination-btn"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              ‹
            </button>

            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let page: number;
              if (totalPages <= 5) {
                page = i + 1;
              } else if (currentPage <= 3) {
                page = i + 1;
              } else if (currentPage >= totalPages - 2) {
                page = totalPages - 4 + i;
              } else {
                page = currentPage - 2 + i;
              }
              return (
                <button
                  key={page}
                  className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                  onClick={() => handlePageChange(page)}
                >
                  {page}
                </button>
              );
            })}

            <button
              className="pagination-btn"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              ›
            </button>
            <button
              className="pagination-btn"
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
            >
              »
            </button>
          </div>

          <div className="page-size-selector">
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
            <span>{t('common.perPage')}</span>
          </div>
        </div>
      )}

      <style>{`
        .projects-table-container {
          background: var(--color-surface);
          border-radius: var(--radius-lg);
          box-shadow: var(--shadow);
          overflow: hidden;
        }

        .table-wrapper {
          overflow-x: auto;
        }

        .projects-table {
          width: 100%;
          border-collapse: collapse;
        }

        .projects-table th,
        .projects-table td {
          padding: 0.75rem 0.5rem;
          text-align: start;
          border-bottom: 1px solid var(--color-border);
          white-space: nowrap;
        }

        .projects-table th {
          background: var(--color-background);
          font-weight: 600;
          font-size: 0.75rem;
          text-transform: uppercase;
          color: var(--color-text-secondary);
        }

        .projects-table th:first-child,
        .projects-table td:first-child {
          padding-inline-start: 1rem;
        }

        .projects-table th:last-child,
        .projects-table td:last-child {
          padding-inline-end: 1rem;
        }

        .col-expand {
          width: 40px;
        }

        .col-actions {
          width: 50px;
        }

        .text-end {
          text-align: end;
        }

        .pagination {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0.75rem 1rem;
          border-top: 1px solid var(--color-border);
          background: var(--color-background);
          gap: 1rem;
          flex-wrap: wrap;
        }

        .pagination-info {
          font-size: 0.875rem;
          color: var(--color-text-secondary);
        }

        .pagination-controls {
          display: flex;
          gap: 0.25rem;
        }

        .pagination-btn {
          min-width: 32px;
          height: 32px;
          padding: 0 0.5rem;
          border: 1px solid var(--color-border);
          border-radius: var(--radius);
          background: var(--color-surface);
          color: var(--color-text);
          font-size: 0.875rem;
          cursor: pointer;
          transition: all 0.2s;
        }

        .pagination-btn:hover:not(:disabled) {
          background: var(--color-primary);
          border-color: var(--color-primary);
          color: white;
        }

        .pagination-btn.active {
          background: var(--color-primary);
          border-color: var(--color-primary);
          color: white;
        }

        .pagination-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .page-size-selector {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.875rem;
          color: var(--color-text-secondary);
        }

        .page-size-selector select {
          padding: 0.25rem 0.5rem;
          border: 1px solid var(--color-border);
          border-radius: var(--radius);
          background: var(--color-surface);
        }
      `}</style>

      {editingProject && (
        <ProjectFormModal
          isOpen={true}
          onClose={() => setEditingProject(null)}
          project={editingProject}
        />
      )}
    </div>
  );
};
